import { Button, ButtonType } from '@UI/components/controls/Button/Button.tsx'
import { CalendarDays } from '@UI/components/icons/CalendarDays.tsx'
import { Search } from '@UI/components/icons/Search.tsx'
import { Colors } from '@UI/theme/generated/Colors.ts'
import { Body_16_Coral_Strong_emphasis_16 } from '@UI/typography/generated/typography.tsx'
import { FilterCalendar } from '@src/CommonComponents/Calendar/FilterCalendar.tsx'
import { ClearResultsButton } from '@src/CommonComponents/ClearResultsButton/ClearResultsButton.tsx'
import { Input, InputHandlers } from '@src/CommonComponents/Input/InputNative.tsx'
import { Stack } from '@src/CommonComponents/Stack/Stack.tsx'
import { ButtonVar, PeriodLabel } from '@src/Pages/Views/Common.tsx'
import { SortingButton } from '@src/Pages/Views/Employee/AttendanceHistory/AttendanceHistoryPage.tsx'
import { DailyEvents } from '@src/Pages/Views/Manager/AttendanceHistory/DailyEvents/DailyEvents.tsx'
import { PeriodEvents } from '@src/Pages/Views/Manager/AttendanceHistory/PeriodEvents/PeriodEvents.tsx'
import { EmployeeWithWorkdays, WorkDayWithEvents } from '@src/Store/employeesStore.ts'
import { useEventsStore } from '@src/Store/eventsStore.ts'
import {
    EmployeeEvents,
    WorkEventKindEnum as KindEnum,
    WorkdayStatusEnum as StatusEnum,
    WorkEventInline
} from '@src/httpClient/__generated__/Api.ts'
import { Gt } from '@src/i18n/Gt.tsx'
import { formatDate } from '@src/utils/formatDate.ts'
import cn from 'classnames'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styles from './attendanceHistoryPage.module.less'

const FiltersBlock = ({ setSelectedPeriod }: { setSelectedPeriod: (period: ButtonVar) => void }) => {
    const [params] = useSearchParams()
    const navigate = useNavigate()
    const searchRef = useRef<InputHandlers>(null)
    const isEventsLoading = useEventsStore(x => x.isLoading)

    const [selectedButton, setSelectedButton] = useState<ButtonVar>(ButtonVar.today)

    useEffect(() => {
        setSelectedPeriod(selectedButton)
    }, [selectedButton])

    useEffect(() => {
        const p = new URLSearchParams(params)
        p.delete('date')
        p.delete('date_range_after')
        p.delete('date_range_before')
        p.delete('record_range_after')
        p.delete('record_range_before')
        switch (selectedButton) {
            case ButtonVar.today: {
                p.set('date', formatDate(new Date().toISOString()))
                p.set('record_range_after', formatDate(new Date().toISOString()))
                break
            }
            case ButtonVar.thisWeek: {
                const dt = new Date()
                dt.setDate(dt.getDate() - dt.getDay())
                p.set('date_range_after', formatDate(dt.toISOString()))
                p.set('record_range_after', formatDate(dt.toISOString()))
                break
            }
            case ButtonVar.thisMonth: {
                const dt = new Date()
                dt.setDate(1)
                p.set('date_range_after', formatDate(dt.toISOString()))
                p.set('record_range_after', formatDate(dt.toISOString()))
                break
            }
        }
        navigate(`?${p.toString()}`, { replace: true })
    }, [selectedButton])

    const searchValue = useMemo(() => {
        const val = params.get('search') ?? ''
        if (val?.length === 0) searchRef?.current?.setValue('')
        return val
    }, [params])

    const searchApply = useCallback(
        (e: string) => {
            if (!e && e?.length === 0) e = ''
            const p = new URLSearchParams(params)
            p.set('search', e)
            navigate(`?${p.toString()}`, { replace: true })
        },
        [params]
    )

    const clear = () => {
        if (!searchRef?.current) return
        const p = new URLSearchParams(params)
        p.delete('search')
        navigate(`?${p.toString()}`, { replace: true })
        searchRef.current.clear()
    }

    return (
        <div className={styles.filtersBlock}>
            <Stack direction={'column'} gap={16} className={styles.fixedFilters}>
                <Stack gap={8} alignItems={'center'}>
                    <Input
                        grow
                        ref={searchRef}
                        leftIcon={<Search size={16} color={Colors.Obsidian_Dark_50} />}
                        rightIcon={searchValue ? <ClearResultsButton clickEvent={clear} /> : null}
                        name={'search'}
                        placeholder={t('Search')}
                        className={styles.searchInput}
                        wrapperClassName={styles.searchBox}
                        initialValue={searchValue}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                // @ts-ignore
                                searchApply(e.target.value)
                            }
                        }}
                    />

                    <Button
                        type={ButtonType.clickableIcon}
                        className={cn(styles.showCalendarButton, {
                            [styles.activeCalendarButton]: selectedButton === ButtonVar.manual
                        })}
                        onClick={() =>
                            setSelectedButton(selectedButton === ButtonVar.manual ? ButtonVar.today : ButtonVar.manual)
                        }
                        icon={<CalendarDays size={30} />}
                    />
                </Stack>
                {selectedButton === ButtonVar.manual && <FilterCalendar isLoading={isEventsLoading} />}
                <Stack className={styles.buttonsGroup} justifyContent={'center'}>
                    <Button
                        type={ButtonType.third}
                        onClick={() => setSelectedButton(ButtonVar.today)}
                        className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.today })}
                    >
                        <Gt>Today</Gt>
                    </Button>
                    <Button
                        type={ButtonType.third}
                        onClick={() => setSelectedButton(ButtonVar.thisWeek)}
                        className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.thisWeek })}
                    >
                        <Gt>This week</Gt>
                    </Button>
                    <Button
                        type={ButtonType.third}
                        onClick={() => setSelectedButton(ButtonVar.thisMonth)}
                        className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.thisMonth })}
                    >
                        <Gt>This month</Gt>
                    </Button>
                </Stack>
            </Stack>
        </div>
    )
}

let nextWorkdayId = 1

const mapEmployeeEvents = (data: EmployeeEvents[]): EmployeeWithWorkdays[] => {
    return data.map(ee => {
        const flatEvents: WorkEventInline[] = []
        for (const pair of ee.events) {
            if (pair.event_in) flatEvents.push(pair.event_in)
            if (pair.event_out) flatEvents.push(pair.event_out)
        }
        const eventsByDate = new Map<string, WorkEventInline[]>()
        for (const ev of flatEvents) {
            const day = ev.recorded_at.slice(0, 10)
            const arr = eventsByDate.get(day)
            if (arr) arr.push(ev)
            else eventsByDate.set(day, [ev])
        }

        const workdays: WorkDayWithEvents[] = Array.from(eventsByDate.entries()).map(([date, events]) => {
            const hasIn = events.some(e => e.kind === KindEnum.In)
            const hasOut = events.some(e => e.kind === KindEnum.Out)
            return {
                id: nextWorkdayId++,
                date,
                start_time: events.find(e => e.kind === KindEnum.In)?.recorded_at ?? null,
                end_time: [...events].reverse().find(e => e.kind === KindEnum.Out)?.recorded_at ?? null,
                status: hasOut ? StatusEnum.Finished : hasIn ? StatusEnum.Working : StatusEnum.New,
                total_time: null,
                inconsistent: false,
                ad_hoc: false,
                events
            }
        })

        return {
            id: ee.employee.id,
            first_name: ee.employee.first_name,
            last_name: ee.employee.last_name,
            workdays
        } as EmployeeWithWorkdays
    })
}

export const ManagerView = () => {
    const [selectedPeriod, setSelectedPeriod] = useState<ButtonVar>(ButtonVar.today)
    const [sortingRule, setSortingRule] = useState<'ASC' | 'DESC'>('DESC')
    const employeeEventsData = useEventsStore(x => x.employeeEvents)

    const employeeEvents: EmployeeWithWorkdays[] = useMemo(() => {
        return mapEmployeeEvents(employeeEventsData)
    }, [employeeEventsData])

    const sorted = useMemo(() => {
        if (!employeeEvents) return []
        const factor = sortingRule === 'DESC' ? 1 : -1
        return [...employeeEvents].sort((a, b) => {
            const lastCmp = (a?.last_name ?? '').localeCompare(b?.last_name ?? '')
            if (lastCmp !== 0) return lastCmp * factor
            return (a?.first_name ?? '').localeCompare(b?.first_name ?? '') * factor
        })
    }, [employeeEvents, sortingRule])

    return (
        <Stack className={styles.root} direction={'column'} gap={16}>
            <FiltersBlock setSelectedPeriod={setSelectedPeriod} />
            <Stack direction={'column'} gap={16} className={styles.eventsBlock}>
                <Stack alignItems={'flex-start'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} className={styles.dateDiapazon}>
                        <Body_16_Coral_Strong_emphasis_16>
                            <PeriodLabel period={selectedPeriod} />
                        </Body_16_Coral_Strong_emphasis_16>
                    </Stack>
                    <SortingButton sortDirection={sortingRule} setSortDirection={setSortingRule} />
                </Stack>
                {selectedPeriod === ButtonVar.today ? <DailyEvents employees={sorted} /> : <PeriodEvents employees={sorted} />}
            </Stack>
        </Stack>
    )
}
